import React, { Component } from 'react';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import { browserHistory } from 'react-router';
import cx from 'classnames';
import { deepExtract } from 'helpers/deep';
import { SearchVenue } from 'features/Search/models/SearchVenue';
import { RESULT_TYPES } from '../Search.constants';
import { PanelSection } from './PanelSection';
import { SearchBoxNoResults } from './Items/SearchBoxNoResults';
import { Favorites } from './Items/Favorites';
import { Activity } from './Items/Activity';
import { Link } from './Items/Link';
import { Venue } from './Items/Venue';
import { Loader } from './Loader';
import { ITEM_TYPES } from './SearchBox.constants';
import css from './SearchBox.scss';

const messages = defineMessages({
  noMatches: {
    id: 'searchbox.item.noMatches',
    defaultMessage: 'No matches',
  },
});

export class KeywordSearchBoxPanel extends Component {
  getRenderMethodForItem({ type }) {
    return {
      [ITEM_TYPES.venue]: this.renderVenue,
      [ITEM_TYPES.activity]: this.renderActivity,
      [ITEM_TYPES.bookmarks]: this.renderFavorites,
      [ITEM_TYPES.links]: this.renderLink,
    }[type];
  }

  getSections() {
    const { canSearchByFavorites, hasQuery, venues, activities, history, resultType } = this.props;

    const sections = [];
    const path = deepExtract('pathname', browserHistory.getCurrentLocation(), '');

    if (!path.startsWith('/search')) {
      sections.push({
        key: 'links',
        items: [
          {
            to: '/search',
            type: ITEM_TYPES.links,
            name: 'Show all classes',
            label: <FormattedMessage id="search.panel.show-all-classes" defaultMessage="Show all classes" />,
          },
        ],
      });
    }

    if (!hasQuery && history.length) {
      sections.push({
        key: 'history',
        items: history,
      });
    }

    if (canSearchByFavorites) {
      const label =
        resultType === RESULT_TYPES.VENUE ? (
          <FormattedMessage id="searchbox.item.favoriteVenues" defaultMessage="Favorite venues" />
        ) : (
          <FormattedMessage id="searchbox.item.favoriteSessions" defaultMessage="Sessions at favorite venues" />
        );
      sections.push({
        key: 'favorites',
        items: [
          {
            type: ITEM_TYPES.bookmarks,
            name: 'Favorites',
            label,
            id: ITEM_TYPES.bookmarks,
          },
        ],
      });
    }

    if (activities.length) {
      sections.push({
        key: 'activities',
        title: hasQuery ? (
          <FormattedMessage id="search.panel.matched-activities" defaultMessage="Activities" />
        ) : (
          <FormattedMessage id="search.panel.popular-activities" defaultMessage="Popular activities" />
        ),
        items: activities,
      });
    }

    if (venues.length) {
      sections.push({
        key: 'venues',
        title: hasQuery ? (
          <FormattedMessage id="search.panel.matched-venues" defaultMessage="Venues" />
        ) : (
          <FormattedMessage id="search.panel.popular-venues" defaultMessage="Popular venues" />
        ),
        items: venues,
      });
    }

    return sections;
  }

  renderLink = (link, highlighted, itemProps) => (
    <Link to={link.to} label={link.label} key="links" highlighted={highlighted} {...itemProps} />
  );

  renderFavorites = (item, highlighted, itemProps) => (
    <Favorites key="favorites" highlighted={highlighted} label={item.label} {...itemProps} />
  );

  renderVenue = (venue, highlighted, itemProps) => (
    <Venue key={venue.id} highlighted={highlighted} onRemoveClick={this.props.onRemoveItem} {...venue} {...itemProps} />
  );

  renderActivity = (activity, highlighted, itemProps) => (
    <Activity
      searchId={activity.search_id}
      key={activity.id}
      nameHighlighted={activity.name_highlighted || activity.name}
      vertical={activity.vertical}
      highlighted={highlighted}
      onRemoveClick={this.props.onRemoveItem}
      {...itemProps}
    />
  );

  renderResults() {
    const { getItemProps, highlightedIndex } = this.props;

    const sections = this.getSections();
    let indexStart = 0;

    return sections.map(({ key, title, items }) => {
      const indexBase = indexStart;
      indexStart += items.length;
      return (
        <PanelSection key={key} title={title}>
          {items.map((item, i) =>
            this.getRenderMethodForItem(item)(item, indexBase + i === highlightedIndex, getItemProps({ item }))
          )}
        </PanelSection>
      );
    });
  }

  render() {
    const { hasResults, intl, isLoading, onPanelScroll } = this.props;

    let sectionItems;
    if (isLoading) {
      sectionItems = <Loader />;
    } else if (hasResults) {
      sectionItems = this.renderResults();
    } else {
      sectionItems = <SearchBoxNoResults message={intl.formatMessage(messages.noMatches)} />;
    }

    return (
      <div className={cx('dropdown', css.searchbox__panel)} onScroll={onPanelScroll}>
        <div className="dropdown__content">{sectionItems}</div>
      </div>
    );
  }
}

KeywordSearchBoxPanel.propTypes = {
  canSearchByFavorites: PropTypes.bool.isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  getItemProps: PropTypes.func.isRequired,
  highlightedIndex: PropTypes.number,
  intl: intlShape.isRequired,
  isLoading: PropTypes.bool,
  hasQuery: PropTypes.bool.isRequired,
  hasResults: PropTypes.bool.isRequired,
  venues: PropTypes.arrayOf(PropTypes.instanceOf(SearchVenue)),
  activities: PropTypes.array.isRequired,
  history: PropTypes.array.isRequired,
  onPanelScroll: PropTypes.func,
  resultType: PropTypes.oneOf(Object.keys(RESULT_TYPES)),
};
